import { FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Dialog, useDialog } from 'components';
import { useResponse } from 'contexts/response';

import { useNewTransaction } from '..';
import { TransactionStatus } from '../new-transaction.context';

/**
 * Shown when the save did not go through.
 *
 * Three things an operator might reasonably want, and all three are reachable:
 *
 * - **edit** — keep everything entered and go back to fix it. Always offered. This is the way out
 *   of a rejected unit number or an expired booking, and it used to be the only button here.
 * - **retry** — send the same thing again. Offered *only* when that could plausibly work: a dropped
 *   connection, a timeout, a 5xx. A 4xx says something about the request is wrong, and re-sending
 *   it unchanged would fail identically every time, so retry there is a loop rather than a way
 *   forward.
 * - **discard** — abandon the transaction. Destructive and deliberately not the default.
 *
 * The retry carries the same idempotency key as the original attempt, so if that attempt in fact
 * committed and only its response was lost, the server answers with the transaction it already
 * created rather than making a second one.
 *
 * Why the care: the alternative for a gate operator is re-keying a full transaction — units,
 * damages, photos, tire inspections — at a truck lane, with a driver waiting.
 */
const TransactionFailedDialog: FC = () => {
  const navigate = useNavigate();
  const { closeDialog, openDialog, ref } = useDialog();
  const { status, setStatus, retrySave, canRetry } = useNewTransaction();
  const { error, clearError } = useResponse();

  useEffect(() => {
    if (!ref.current?.open && status === TransactionStatus.FAILED) openDialog();
    if (ref.current?.open && status !== TransactionStatus.FAILED) closeDialog();
  }, [ref, status, closeDialog, openDialog]);

  const handleCancel = useCallback(() => {
    clearError();
    navigate('/gate');
  }, [clearError, navigate]);

  /* Keeps the operator on the page with their data intact — nothing was saved, so it is the only copy. */
  const handleEdit = useCallback(() => {
    clearError();
    setStatus(undefined);
  }, [clearError, setStatus]);

  const handleRetry = useCallback(() => {
    clearError();
    retrySave();
  }, [clearError, retrySave]);

  return (
    <Dialog closeDialog={handleEdit} ref={ref}>
      <div id="failed-dialog" className="d-flex flex-column g-3">
        <h3 className="text-info-dark font-weight-medium">Transaction Failed</h3>
        <p className="text-info-main">{error ?? 'Error'}</p>
        <p className="text-info-main">
          {canRetry
            ? 'Nothing was saved — the transaction and its photos are stored together or not at all, so there is no partial record to clean up. Your entries are still here.'
            : 'Nothing was saved. Correct the problem above and submit again — your entries are still here.'}
        </p>
        <div className="d-flex g-2 justify-content-end mt-3">
          <button className="btn-danger" type="reset" onClick={handleCancel}>
            discard
          </button>
          <button
            className={canRetry ? 'btn-outline' : 'btn-primary'}
            type="button"
            onClick={handleEdit}
            data-cy="edit-transaction"
          >
            edit
          </button>
          {canRetry && (
            <button className="btn-primary" type="button" onClick={handleRetry} data-cy="retry-save">
              retry
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default TransactionFailedDialog;
